import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import GoogleButton from 'react-google-button';
import bgImage from '../assets/images/bgImg1.png';
import bgBase from '../assets/images/bgBase.png';
import { addLogin, updateLoading } from '../store/authentication/loginSlice';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { login, getProjectList, getAllOrgsListAPI, getMyPermissions, APP_STRINGS } from '../constants';
import { refreshScopedPermissions, setStoredPermissions, PRIVILEGES } from '../utils/permissions';
import {
  setProjectList,
  setSelectedOrganization,
  setOrganizationList,
  setSprintList,
  setReleasesList,
  setRepoList,
  setBoardList,
  setSelectedProject,
  setSelectedSprint,
  setSelectedRelease,
  setSelectedTypeValue,
} from '../store/JiraSlices/jiraSlice';
import { setSelectedRepository } from '../store/GitSlices/gitSlices';
import { storeBoardInSession } from '../utils/boardUtils';
import { applyDefaultSelectionToSession, pickDefaultProjectForBootstrap } from '../utils/defaultSelectionUtils';

const normalizeRoleKey = (role) =>
  String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

/** Same rule as sidebar Admin menu — privilege or Admin / Super Admin role */
const hasAdminPageAccess = (permissions, role) => {
  const privileges = permissions?.privileges || permissions?.can || [];
  if (Array.isArray(privileges) && privileges.includes(PRIVILEGES.ADMIN_BLOCK)) return true;
  if (privileges && typeof privileges === 'object' && !Array.isArray(privileges) && privileges[PRIVILEGES.ADMIN_BLOCK]) {
    return true;
  }
  const key = normalizeRoleKey(permissions?.role || role);
  return key === 'admin' || key === 'super admin';
};

const resolvePostLoginPath = ({ permissions, role, hasProjects, redirectUrl }) => {
  if (hasAdminPageAccess(permissions, role)) return '/admin/project-access';
  if (hasProjects) return redirectUrl || '/standUp';
  return '/integration';
};

export default function SignIn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [initialLoginValues] = useState({ email: '', password: '' });
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const validationSchema = object({
    email: string()
      .email('Invalid email address')
      .required('Email is required'),
    password: string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+}{"':;?/>.<,]).*$/,
        'Password must contain at least:\nOne Uppercase letter\nOne Lowercase letter\nOne Number\nOne Special character',
      ),
  });

  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      const params = new URLSearchParams(location.search);
      const googleError = params.get('googleError');
      if (googleError) {
        toast.error(decodeURIComponent(googleError), {
          className: 'bg-secondary-500 text-white',
        });
      }

      const token = params.get('token');
      const companyId = params.get('companyId');
      const companyNameParam = params.get('companyName');
      const username = params.get('username');
      const useremail = params.get('useremail');
      const role = params.get('role');

      if (token && companyId) {
        sessionStorage.setItem('qmetrix-token', token);
        sessionStorage.setItem('companyId', companyId);
        sessionStorage.setItem('orgId', companyId);
        sessionStorage.setItem('syncStatus', 'success');
        sessionStorage.removeItem('hasRefreshed');
        sessionStorage.setItem(
          'userData',
          JSON.stringify({
            name: username,
            email: useremail,
            role: role || '',
            companyId,
          }),
        );

        let orgName = String(companyNameParam || '').trim();
        try {
          const fetchedOrgs = await getAllOrgsListAPI();
          const orgList = Array.isArray(fetchedOrgs) ? fetchedOrgs : [];
          if (orgList.length > 0) {
            dispatch(setOrganizationList(orgList));
            queryClient.setQueryDefaults(['getAllOrgsListAPI'], { staleTime: 30 * 60 * 1000 });
            queryClient.setQueryData(['getAllOrgsListAPI'], orgList);
            const selected = orgList.find((o) => String(o._id) === String(companyId));
            if (selected?.companyName) {
              orgName = selected.companyName;
            }
          }
        } catch (orgErr) {
          console.error('Failed to fetch organization list after Google login:', orgErr);
        }
        if (!orgName) {
          orgName = String(companyId);
        }

        sessionStorage.setItem('orgName', orgName);
        dispatch(
          setSelectedOrganization({
            selectedOrgName: orgName,
            selectedOrgId: companyId,
          }),
        );

        try {
          const projectList = await getProjectList();
          const projects = projectList?.data || projectList || [];
          let permPayload = null;
          if (Array.isArray(projects) && projects.length) {
            dispatch(setProjectList(projects));
            const defaultProject = pickDefaultProjectForBootstrap(projects);
            if (defaultProject) {
              sessionStorage.setItem('projectId', defaultProject._id);
              sessionStorage.setItem('projectName', defaultProject.name);
              dispatch(
                setSelectedProject({
                  selectedProjectName: defaultProject.name,
                  selectedProjectId: defaultProject._id,
                }),
              );
              permPayload = await refreshScopedPermissions(companyId, defaultProject._id);
            } else {
              const permRes = await getMyPermissions(companyId, null);
              permPayload = permRes?.data || permRes;
              setStoredPermissions(permPayload);
            }
          } else {
            const permRes = await getMyPermissions(companyId, null);
            permPayload = permRes?.data || permRes;
            setStoredPermissions(permPayload);
          }
          const googlePostPath = resolvePostLoginPath({
              permissions: permPayload,
              role,
              hasProjects: Array.isArray(projects) && projects.length > 0,
              redirectUrl: '/standUp',
            });
          if (String(googlePostPath).startsWith('/admin')) {
            sessionStorage.setItem('lastVisitedPath', googlePostPath);
          }
          navigate(googlePostPath);
        } catch (err) {
          console.error('Failed to load projects/permissions after Google login:', err);
          const googleFallbackPath = resolvePostLoginPath({
              permissions: null,
              role,
              hasProjects: false,
              redirectUrl: '/standUp',
            });
          if (String(googleFallbackPath).startsWith('/admin')) {
            sessionStorage.setItem('lastVisitedPath', googleFallbackPath);
          }
          navigate(googleFallbackPath);
        }
      }
    };

    fetchData();
  }, [location, dispatch, navigate, queryClient]);

  useEffect(() => {
    dispatch(updateLoading());
  }, [dispatch]);
  const mutateAsync = useMutation({
    mutationFn: login,
    onSuccess: async (data) => {
      const token = data?.data?.token;
      const companyId = data?.data?.companyId || null;

      queryClient.invalidateQueries(['addLogin']);
      sessionStorage.setItem('qmetrix-token', token);
      sessionStorage.setItem('syncStatus', 'success');
      // Only seed session company from mapping-backed login companyId (never stale Meta home alone).
      if (companyId) {
        sessionStorage.setItem('companyId', companyId);
        sessionStorage.setItem('orgId', companyId);
      } else {
        sessionStorage.removeItem('companyId');
        sessionStorage.removeItem('orgId');
        sessionStorage.removeItem('orgName');
        sessionStorage.removeItem('projectId');
        sessionStorage.removeItem('projectName');
      }
      if (data?.data) {
        sessionStorage.setItem(
          'userData',
          JSON.stringify({
            name: data.data.name,
            email: data.data.email,
            role: data.data.role,
            id: data.data.id,
            companyId: companyId || null,
          }),
        );
      }
      // Prefer project-scoped permissions from login (default project).
      // Final refresh runs again after defaultProject is applied below.
      if (data?.data?.permissions) {
        setStoredPermissions({
          ...data.data.permissions,
          role: data.data.permissions.role || data.data.role,
        });
      }
      // Allow Stand-up mount restore to fetch card data after same-tab re-login
      sessionStorage.removeItem('hasRefreshed');
      const navData = data?.data?.navigationData;
      const companyName =
        (companyId &&
          (navData?.companyName || data?.data?.workspace?.workspaceName || '')) ||
        '';

      dispatch(
        setSelectedOrganization({
          selectedOrgName: companyName,
          selectedOrgId: companyId || '',
        }),
      );
      if (companyName) {
        sessionStorage.setItem('orgName', companyName);
      }

      if (navData?.status === 'success') {
        const projectList = Array.isArray(navData.projectList) ? navData.projectList : [];
        dispatch(setProjectList(projectList));

        let orgList = Array.isArray(navData.allOrgs) ? navData.allOrgs : [];
        if (orgList.length === 0 && companyId) {
          try {
            const fetchedOrgs = await getAllOrgsListAPI();
            orgList = Array.isArray(fetchedOrgs) ? fetchedOrgs : [];
          } catch (orgErr) {
            console.error('Failed to fetch organization list on login:', orgErr);
          }
        }
        // Always update (including empty) so a revoked workspace is not left in Redux.
        dispatch(setOrganizationList(orgList));
        queryClient.setQueryDefaults(['getAllOrgsListAPI'], { staleTime: 30 * 60 * 1000 });
        queryClient.setQueryData(['getAllOrgsListAPI'], orgList);

        let dp = navData.defaultProject;
        // Fallback: ensure a default project is selected when API omitted defaultProject
        if (!dp?.projectId && projectList.length > 0) {
          const fallback = pickDefaultProjectForBootstrap(projectList);
          if (fallback?._id) {
            dp = {
              projectId: fallback._id,
              projectName: fallback.name,
              defaultBoard: null,
              boardList: [],
            };
          }
        }

        if (dp?.projectId) {
          sessionStorage.setItem('projectId', dp.projectId);
          sessionStorage.setItem('projectName', dp.projectName);
          dispatch(setSelectedProject({
            selectedProjectName: dp.projectName,
            selectedProjectId: dp.projectId,
          }));

          if (dp.boardList?.length > 0) {
            dispatch(setBoardList(dp.boardList));
          }

          if (dp.lastSynced != null) {
            const syncKey = `syncStatus_${companyId}_${dp.projectId}`;
            const timeKey = `lastSyncTime_${companyId}_${dp.projectId}`;
            sessionStorage.setItem(timeKey, dp.lastSynced);
            sessionStorage.setItem(syncKey, dp.syncStatus ? 'success' : 'failed');
          }

          const db = dp.defaultBoard;
          // Resolve menus for the default project role (Employee on QMetry ≠ SM on LMS)
          try {
            await refreshScopedPermissions(companyId, dp.projectId);
          } catch (permErr) {
            console.error('Failed to refresh project-scoped permissions on login:', permErr);
          }

          if (db) {
            storeBoardInSession(db.boardId, db.boardName, db.boardType || '');
            if (db.boardId && dp.projectId) {
              sessionStorage.setItem('boardScopeProjectId', String(dp.projectId));
            }

            const sprintList = db.sprintList || [];
            const releaseList = db.releaseList || [];
            const repoList = db.repoList || [];

            dispatch(setSprintList(sprintList));
            dispatch(setReleasesList(releaseList));
            dispatch(setRepoList(repoList));

            const selection = applyDefaultSelectionToSession({
              boardType: db.boardType,
              sprintList,
              releaseList,
              repoList,
            });

            if (selection.mode === 'release' && selection.release) {
              dispatch(
                setSelectedRelease({
                  selectedReleaseId: selection.release._id,
                  selectedReleaseName: selection.release.releaseName,
                }),
              );
              dispatch(
                setSelectedTypeValue({
                  selectedValueLabel: selection.releaseLabel,
                  selectedValue: APP_STRINGS.VALUE_RELEASE,
                }),
              );
            } else if (selection.mode === 'sprint' && selection.sprint) {
              dispatch(
                setSelectedSprint({
                  selectedSprintId: selection.sprint._id,
                  selectedSprintName: selection.sprint.name,
                }),
              );
              dispatch(
                setSelectedTypeValue({
                  selectedValueLabel: selection.sprintLabel,
                  selectedValue: APP_STRINGS.VALUE_SPRINT,
                }),
              );
            }

            if (repoList.length > 0) {
              const defaultRepo = [...repoList].sort()[0];
              dispatch(setSelectedRepository(defaultRepo));
            }
          }
        }
      } else {
        if (navData?.error) {
          toast.warn(`Navigation data: ${navData.error}`, {
            className: 'bg-secondary-500 text-white',
          });
        }
        if (companyId) {
          try {
            const projectListRes = await getProjectList(companyId);
            const projectList = Array.isArray(projectListRes?.data)
              ? projectListRes.data
              : Array.isArray(projectListRes)
                ? projectListRes
                : [];
            dispatch(setProjectList(projectList));
            const fallback = pickDefaultProjectForBootstrap(projectList);
            if (fallback?._id) {
              sessionStorage.setItem('projectId', fallback._id);
              sessionStorage.setItem('projectName', fallback.name);
              dispatch(
                setSelectedProject({
                  selectedProjectName: fallback.name,
                  selectedProjectId: fallback._id,
                }),
              );
              try {
                await refreshScopedPermissions(companyId, fallback._id);
              } catch (permErr) {
                console.error('Failed to refresh permissions for fallback project:', permErr);
              }
            }
          } catch (error) {
            console.error('Failed to fetch project list:', error);
          }
        } else {
          dispatch(setProjectList([]));
        }
        let orgList = Array.isArray(navData?.allOrgs) ? navData.allOrgs : [];
        if (orgList.length === 0 && companyId) {
          try {
            const fetchedOrgs = await getAllOrgsListAPI();
            orgList = Array.isArray(fetchedOrgs) ? fetchedOrgs : [];
          } catch (orgErr) {
            console.error('Failed to fetch organization list on login:', orgErr);
          }
        }
        dispatch(setOrganizationList(orgList));
        queryClient.setQueryDefaults(['getAllOrgsListAPI'], { staleTime: 30 * 60 * 1000 });
        queryClient.setQueryData(['getAllOrgsListAPI'], orgList);
      }

      dispatch(addLogin(data));

      const hasProjects = navData?.projectList?.length > 0;
      const loginPermissions = data?.data?.permissions;
      const loginRole = data?.data?.role || loginPermissions?.role;
      const postLoginPath = resolvePostLoginPath({
        permissions: loginPermissions,
        role: loginRole,
        hasProjects,
        redirectUrl: data.redirectUrl,
      });
      // So Admin → Standup bootstrap can detect first landing even on slow networks
      if (String(postLoginPath).startsWith('/admin')) {
        sessionStorage.setItem('lastVisitedPath', postLoginPath);
      }

      if (data.success === true) {
        toast.success(data.message, {
          className: 'bg-secondary-500 text-white',
        });
        setTimeout(() => {
          navigate(postLoginPath);
        }, 1000);
      } else {
        navigate(postLoginPath);
      }
    },
    onError: (error) => {
      if (
        (error.response?.status === 401 || error.response?.status === 404) &&
        error.response?.data?.success === false
      ) {
        toast.info(error.response?.data?.message, {
          className: 'bg-secondary-500 text-white',
        });
        const redirectUrl = error.response?.data?.redirectUrl;
        if (redirectUrl) {
          setTimeout(() => {
            navigate(redirectUrl);
          }, 1000);
        }
      }
    },
  });

  // Busy while the request/post-login bootstrap runs, and after success until the redirect happens.
  const isLoginBusy = mutateAsync.isPending || mutateAsync.isSuccess;

  const formik = useFormik({
    initialValues: initialLoginValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Ignore repeat submits (button click or Enter key) while login is in flight / redirecting
      if (isLoginBusy) return;
      const payload = {
        ...values,
        email: values.email.toLowerCase(),
      };
      try {
        mutateAsync.mutate(payload);
      } catch (error) {
        toast.error(error);
      }
    },
  });

  const googleSignIn = () => {
    const googleLoginURL = `/api/user/auth/google`;
    window.location.href = googleLoginURL;
  };
  const handleNavigate = () => {
    dispatch(updateLoading());
    navigate('/register');
  };

  return (
    <div className="bg-secondary-600 flex flex-row w-full overflow-hidden">
      <div className="relative md:w-[55%] lg:w-[62%]">
        <div className="relative h-screen flex">
          <img src={bgBase} className="fixed bottom-0 left-0 w-full h-1/3" alt="Surface-image" />
          <img
            src={bgImage}
            className="relative bg-no-repeat bg-fixed bg-center bg-cover w-2/3 ml-[25%] my-auto"
            alt="Background-image"
          />
        </div>
      </div>
      <div className="bg-secondary-500 w-[45%] lg:w-[38%] h-screen relative flex">
        <div className="relative my-auto w-4/5 ml-[10%]">
          <div className="flex flex-col relative w-full mb-4">
            <h1 className="text-2xl font-semibold text-gray-300 flex items-center">
              Welcome to QMetry ! 👋🏻
            </h1>
            <h1 className="text-base text-gray-400">
              Please sign-in to your account and start the adventure
            </h1>
          </div>
          <div className="relative">
            <form onSubmit={formik.handleSubmit}>
              <div className="sm:col-span-4 w-full">
                <label
                  htmlFor="email"
                  className={`block text-sm mb-2 font-medium leading-6 ${formik.touched.email && formik.errors.email
                      ? 'text-red-500'
                      : isEmailFocused
                        ? 'text-blue-500'
                        : 'text-gray-300'
                    }`}
                >
                  Email
                </label>
                <div>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={() => setIsEmailFocused(false)}
                    onFocus={() => setIsEmailFocused(true)}
                    className={`text-gray-200 w-full border-gray-500 outline-none hover:border-gray-300 rounded-md bg-transparent py-1.5 pl-1 placeholder:text-gray-400 sm:text-sm sm:leading-6 ${formik.touched.email && formik.errors.email
                        ? 'border-none outline-1 outline-red-500'
                        : ''
                      }`}
                    placeholder="Email Address"
                  />
                </div>
                {formik.touched.email && formik.errors.email ? (
                  <div className="text-red-500 text-sm mt-1">{formik.errors.email}</div>
                ) : null}
              </div>
              <div className="sm:col-span-4 w-full mt-6">
                <label
                  htmlFor="password"
                  className={`block text-sm mb-2 font-medium leading-6 ${formik.touched.password && formik.errors.password
                      ? 'text-red-500'
                      : isPasswordFocused
                        ? 'text-blue-500'
                        : 'text-gray-300'
                    }`}
                >
                  Password
                </label>
                <div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    id="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={() => setIsPasswordFocused(false)}
                    onFocus={() => setIsPasswordFocused(true)}
                    className={`text-gray-200 w-full border-gray-500 outline-none hover:border-gray-300 rounded-md bg-transparent py-1.5 pl-1 placeholder:text-gray-400 sm:text-sm sm:leading-6 ${formik.touched.password && formik.errors.password
                        ? ' border-none outline-1 outline-red-500'
                        : ''
                      }`}
                    placeholder="********"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute mx-auto mt-5 text-blue-500 opacity-75 right-[1%] pr-3 bg-transparent transform -translate-y-1/2 focus:outline-none"
                  >
                    {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password ? (
                  <div className="text-red-500 text-sm mt-1">{formik.errors.password}</div>
                ) : null}
              </div>
              <div className=" mt-6 mb-4 flex flex-row items-center justify-between w-full">
                <span className="text-gray-300">
                  <input
                    type="checkbox"
                    className="appearance-none mr-2 bg-transparent rounded-md border-gray-400 hover:border-gray-300 cursor-pointer"
                  />
                  Remember me
                </span>
                <Link to="/forgotPassword">
                  <span className="block text-blue-500 font-normal cursor-pointer">
                    Forgot Password ?
                  </span>
                </Link>
              </div>
              <button
                type="submit"
                className={`w-full p-2 rounded-md bg-blue-500 text-white font-semibold hover:bg-blue-600 transition duration-300 ${isLoginBusy ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                disabled={isLoginBusy}
              >
                {isLoginBusy ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5 text-white mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 4.75v1.5M6.343 6.343l1.06 1.06M4.75 12h1.5M6.343 17.657l1.06-1.06M12 18.25v1.5M17.657 17.657l-1.06-1.06M18.25 12h-1.5M17.657 6.343l-1.06 1.06"
                      />
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Login'
                )}
              </button>
            </form>
          </div>

          {process.env.REACT_APP_ENV === 'production' && (
            <>
              <div className="flex w-1/2 mx-auto justify-evenly text-xl mt-2">
                <div className="hover:bg-gray-600 cursor-pointer mt-5">
                  <GoogleButton
                    style={{
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '10px',
                      paddingRight: '0px',
                    }}
                    onClick={googleSignIn}
                  />
                </div>
              </div>
            </>
          )}

          <div className="relative mt-5 flex justify-center items-center gap-0.5">
            <span className="text-gray-300 lg:mr-2">New to our platform? </span>
            <Link onClick={handleNavigate}>
              <span className="text-blue-500 font-normal cursor-pointer">Create an Account</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}